import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Trash2, Minus, Plus, ShoppingBag, ArrowRight,
  Package, AlertCircle, ChevronLeft,
} from "lucide-react";
import {
  selectCartItems,
  selectCartTotal,
  selectCartShippingTotal,
  selectCartGrandTotal,
  removeItem,
  updateQuantity,
  clearCart,
  setCheckoutError,
  clearCheckoutError,
  selectCheckoutError,
} from "../store/cartSlice";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";
import AbandonedCartBanner from "../components/cart/AbandonedCartBanner";

const fmtPrice = (v) =>
  v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ── Line item row ─────────────────────────────────────────────────────── */
function CartLineItem({ item }) {
  const dispatch = useDispatch();
  const { product, quantity, selectedColor, shippingOption, shippingCost } = item;
  const pid      = product.mongo_id || product.id;
  const img      = product.photo_urls?.[0] || product.img_url;
  const slug     = product.slug || pid;
  const price = product.sale_price || 0;
  const hasDiscount =
    product.original_price && product.sale_price && product.sale_price < product.original_price;

  const shippingLabel = shippingOption === 'express'
    ? `Express +${(shippingCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : shippingOption === 'sameday'
    ? `Same-Day +${(shippingCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : 'Free Shipping';

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: "20px 0",
        borderBottom: "1px solid hsl(var(--border))",
        alignItems: "flex-start",
      }}>
      {/* Image */}
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: 12,
          overflow: "hidden",
          flexShrink: 0,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}>
        {img ? (
          <img
            src={img}
            alt={product.product_name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Package size={24} style={{ color: "var(--vb-soft-v)" }} />
          </div>
        )}
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 8,
          }}>
          <div>
            {product.machine_type && (
              <span
                style={{
                  fontSize: 10,
                  fontFamily: "'DM Mono', monospace",
                  color: "var(--vb-indigo)",
                  background: "hsl(var(--secondary))",
                  borderRadius: 6,
                  padding: "1px 7px",
                  display: "inline-block",
                  marginBottom: 4,
                }}>
                {product.machine_type}
              </span>
            )}
            <Link
              to={`/product/${slug}`}
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 600,
                fontSize: 17,
                color: "hsl(var(--foreground))",
                lineHeight: 1.25,
                textDecoration: "none",
                display: "block",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "var(--vb-indigo)";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "hsl(var(--foreground))";
              }}>
              {product.product_name}
            </Link>
            {product.tagline && (
              <p
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 12,
                  color: "hsl(var(--muted-foreground))",
                  marginTop: 3,
                }}>
                {product.tagline}
              </p>
            )}
            {selectedColor && (
              <p
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11,
                  color: "var(--vb-blue)",
                  marginTop: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}>
                Color: {selectedColor}
              </p>
            )}
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                color: shippingOption === "free" ? "#FFF" : "var(--vb-blue)",
                marginTop: 2,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}>
              🚚 {shippingLabel}
            </p>
          </div>

          {/* Unit price */}
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 700,
                fontSize: 16,
                color: "var(--vb-indigo)",
                display: "block",
              }}>
              ${fmtPrice(price * quantity)}
            </span>
            {quantity > 1 && (
              <span
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11,
                  color: "hsl(var(--muted-foreground))",
                }}>
                ${fmtPrice(price)} each
              </span>
            )}
            {hasDiscount && (
              <span
                style={{
                  display: "block",
                  fontSize: 10,
                  color: "hsl(var(--muted-foreground))",
                  textDecoration: "line-through",
                  fontFamily: "'Inter', sans-serif",
                }}>
                ${fmtPrice(product.original_price * quantity)}
              </span>
            )}
          </div>
        </div>

        {/* Qty + remove */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 12,
          }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              border: "1px solid hsl(var(--border))",
              borderRadius: 10,
              padding: "4px 6px",
            }}>
            <button
              onClick={() =>
                dispatch(
                  updateQuantity({ productId: pid, quantity: quantity - 1 }),
                )
              }
              aria-label="Decrease"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 6px",
                display: "flex",
                alignItems: "center",
                color: "hsl(var(--muted-foreground))",
              }}>
              <Minus size={12} />
            </button>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 14,
                fontWeight: 700,
                minWidth: 20,
                textAlign: "center",
              }}>
              {quantity}
            </span>
            <button
              onClick={() =>
                dispatch(
                  updateQuantity({ productId: pid, quantity: quantity + 1 }),
                )
              }
              aria-label="Increase"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 6px",
                display: "flex",
                alignItems: "center",
                color: "hsl(var(--muted-foreground))",
              }}>
              <Plus size={12} />
            </button>
          </div>

          <button
            onClick={() => dispatch(removeItem(pid))}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "hsl(var(--muted-foreground))",
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              padding: "4px 6px",
              borderRadius: 8,
              transition: "color 0.15s",
            }}>
            <Trash2 size={13} />
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Order Summary sidebar ─────────────────────────────────────────────── */
function OrderSummary({ items, total, shippingTotal, grandTotal, onCheckout, loading, error }) {
  const itemCount = items.reduce((s, i) => s + i.quantity, 0);
  const savings = items.reduce((s, i) => {
    if (i.product.original_price && i.product.sale_price) {
      return s + (i.product.original_price - i.product.sale_price) * i.quantity;
    }
    return s;
  }, 0);

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.04)",
        borderRadius: 20,
        border: "1px solid rgba(255,255,255,0.08)",
        padding: "28px 24px",
        position: "sticky",
        top: "calc(var(--header-h, 72px) + 24px)",
      }}>
      <h2
        style={{
          fontFamily: "'Oswald', sans-serif",
          fontWeight: 700,
          fontSize: 20,
          marginBottom: 20,
          color: "hsl(var(--foreground))",
        }}>
        Order Summary
      </h2>

      {/* Line items */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 20,
        }}>
        {items.map(({ product, quantity }) => (
          <div
            key={product.mongo_id || product.id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
            }}>
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                color: "hsl(var(--foreground))",
                flex: 1,
                lineHeight: 1.4,
              }}>
              {product.product_name}
              {quantity > 1 && (
                <span
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    marginLeft: 4,
                  }}>
                  ×{quantity}
                </span>
              )}
            </span>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 13,
                fontWeight: 600,
                flexShrink: 0,
              }}>
              ${fmtPrice((product.sale_price || 0) * quantity)}
            </span>
          </div>
        ))}
      </div>

      {/* Divider */}
      <div
        style={{
          borderTop: "1px solid hsl(var(--border))",
          paddingTop: 16,
          marginBottom: 16,
        }}>
        {savings > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(91,140,255,0.08)",
              border: "1px solid rgba(91,140,255,0.15)",
            }}>
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--vb-blue)",
              }}>
              You're saving
            </span>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 14,
                fontWeight: 700,
                color: "var(--vb-blue)",
              }}>
              -${fmtPrice(savings)}
            </span>
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 6,
          }}>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color: "hsl(var(--muted-foreground))",
            }}>
            Subtotal ({itemCount} item{itemCount !== 1 ? "s" : ""})
          </span>
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
            }}>
            ${fmtPrice(total)}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color: "hsl(var(--muted-foreground))",
            }}>
            Shipping
          </span>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color:
                shippingTotal > 0
                  ? "var(--vb-blue)"
                  : "hsl(var(--muted-foreground))",
            }}>
            {shippingTotal > 0 ? `+${fmtPrice(shippingTotal)}` : "Free"}
          </span>
        </div>
      </div>

      {/* Total */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "14px 0",
          borderTop: "2px solid hsl(var(--border))",
          marginBottom: 12,
        }}>
        <span
          style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            fontSize: 17,
          }}>
          Total
        </span>
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontWeight: 700,
            fontSize: 20,
            color: "var(--vb-indigo)",
          }}>
          ${fmtPrice(grandTotal)}
        </span>
      </div>

      {/* ROI + delivery estimate */}
      <div style={{ marginBottom: 16 }}>
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            color: "var(--vb-blue)",
            opacity: 0.85,
            marginBottom: 4,
          }}>
          All prices are final
        </p>
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            color: "hsl(var(--muted-foreground))",
          }}>
          Estimated delivery: ~21 days after production is complete
        </p>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 14,
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
          }}>
          <AlertCircle
            size={15}
            style={{ color: "#dc2626", flexShrink: 0, marginTop: 1 }}
          />
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: "#dc2626",
              lineHeight: 1.5,
            }}>
            {typeof error === "string"
              ? error
              : error?.message || "Checkout failed. Please try again."}
          </p>
        </div>
      )}

      {/* CTA */}
      {/* Trust block */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          marginBottom: 16,
          padding: "12px 14px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}>
        {[
          { icon: "✔", text: "Secure checkout powered by Stripe" },
          { icon: "✔", text: "SSL encrypted · PCI compliant" },
          { icon: "🚚", text: "Ships in ~21 days after production" },
          { icon: "✔", text: "Trusted by 500+ operators" },
        ].map(({ icon, text }) => (
          <div
            key={text}
            style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 11,
                flexShrink: 0,
                width: 16,
                textAlign: "center",
              }}>
              {icon}
            </span>
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                color: "rgba(255,255,255,0.65)",
              }}>
              {text}
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={onCheckout}
        disabled={loading || items.length === 0}
        style={{
          width: "100%",
          background: loading ? "hsl(var(--muted))" : "var(--grad-primary)",
          color: loading ? "hsl(var(--muted-foreground))" : "#fff",
          border: "none",
          borderRadius: 14,
          padding: "16px 0",
          fontFamily: "'Inter', sans-serif",
          fontSize: 16,
          fontWeight: 700,
          cursor: loading || items.length === 0 ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          transition: "opacity 0.15s",
          boxShadow: "0 4px 20px rgba(91,140,255,0.3)",
        }}>
        Secure Checkout <ArrowRight size={16} />
      </button>
      <p
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 11,
          color: "hsl(var(--muted-foreground))",
          textAlign: "center",
          marginTop: 6,
        }}>
        Takes less than 2 minutes
      </p>

      {/* Payment badges */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          justifyContent: "center",
          marginTop: 12,
        }}>
        {["Visa", "Mastercard", "Amex", "Apple Pay", "Google Pay"].map((n) => (
          <span key={n} className="payment-badge">
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CART PAGE
══════════════════════════════════════════════════════════════════════════ */
export default function CartPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const items = useSelector(selectCartItems);
  const total = useSelector(selectCartTotal);
  const shippingTotal = useSelector(selectCartShippingTotal);
  const grandTotal = useSelector(selectCartGrandTotal);
  const checkoutError = useSelector(selectCheckoutError);
  /* If redirected back from a cancelled Stripe session, show a soft message */
  const wasCancelled = searchParams.get("checkout_cancelled") === "1";

  /* Auto-scroll to top */
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  /* Clear checkout error when cart item count changes.
     dispatch is stable (Redux guarantee), checkoutError is the value we're clearing. */
  useEffect(() => {
    if (checkoutError) dispatch(clearCheckoutError());
  }, [checkoutError, dispatch]);

  // Checkout logic lives in CheckoutPage — cart just navigates there.
  // The API call to /payments/checkout/cart is made after the user
  // fills in their shipping/billing address on the checkout form.
  const handleCheckout = useCallback(() => {
    if (items.length === 0) return;
    dispatch(clearCheckoutError());
    navigate("/checkout");
  }, [items, dispatch, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AbandonedCartBanner />

      <div style={{ paddingTop: "var(--header-h)" }}>
        <div className="max-w-6xl mx-auto px-6 py-12">

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
            <div>
              <h1
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)",
                  color: "hsl(var(--foreground))",
                  lineHeight: 1.1,
                }}
              >
                Your Cart
              </h1>
              {items.length > 0 && (
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "hsl(var(--muted-foreground))", marginTop: 4 }}>
                  {items.reduce((s, i) => s + i.quantity, 0)} item{items.reduce((s, i) => s + i.quantity, 0) !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            <button
              onClick={() => navigate("/shop")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "hsl(var(--muted-foreground))",
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                padding: "8px 0",
              }}
            >
              <ChevronLeft size={15} />
              Continue Shopping
            </button>
          </div>

          {/* Cancelled notice */}
          {wasCancelled && (
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fcd34d",
                borderRadius: 12,
                padding: "12px 16px",
                display: "flex",
                gap: 10,
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <AlertCircle size={16} style={{ color: "#d97706", flexShrink: 0 }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#92400e" }}>
                Your checkout was cancelled. Your cart has been saved — continue when you're ready.
              </p>
            </div>
          )}

          {items.length === 0 ? (
            /* Empty state */
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "80px 24px",
                gap: 16,
                background: "rgba(255,255,255,0.03)",
                borderRadius: 24,
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <ShoppingBag size={52} style={{ color: "var(--vb-soft-v)", opacity: 0.5 }} />
              <p
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 600,
                  fontSize: 22,
                  color: "hsl(var(--foreground))",
                }}
              >
                Your cart is empty
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "hsl(var(--muted-foreground))" }}>
                Browse our full range of smart vending machines.
              </p>
              <button
                onClick={() => navigate("/shop")}
                style={{
                  background: "var(--grad-primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  padding: "12px 28px",
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 8,
                }}
              >
                Browse Machines <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[1fr_380px] gap-10 items-start">
              {/* Left — line items */}
              <div>
                {items.map((item) => (
                  <CartLineItem key={item.product.mongo_id || item.product.id} item={item} />
                ))}

                {/* Clear cart */}
                <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => dispatch(clearCart())}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "hsl(var(--muted-foreground))",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 12,
                      padding: "6px 0",
                    }}
                  >
                    <Trash2 size={12} />
                    Clear cart
                  </button>
                </div>
              </div>

              {/* Right — summary */}
              <OrderSummary
                items={items}
                total={total}
                shippingTotal={shippingTotal}
                grandTotal={grandTotal}
                onCheckout={handleCheckout}
                loading={false}
                error={checkoutError}
              />
            </div>
          )}
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
